import React, { useContext, useState } from 'react';
import { PremiumAssetType } from '@wix/ambassador-premium-billing-and-payments/types';
import {
  ChargeInfo,
  SubscriptionInfo,
} from '@wix/ambassador-serverless-charge-locator-service/rpc';

type TPageErrors = { hasError: boolean; message: string | JSX.Element };

export type TChargeDetails = {
  isConsolidated: boolean;
  service: PremiumAssetType;
  description: string;
  currencyCode: string;
  amount: number;
  date: Date;
  subscriptionPeriod: SubscriptionInfo | undefined;
  domainName: string;
  quantity: number;
  invoiceNumber: string;
  accountEmailMasked: string;
  paymentMethod: {
    label: string;
    type: string;
  };
};

export const emptyCharge: TChargeDetails = {
  isConsolidated: false,
  service: PremiumAssetType.UNKNOWN,
  description: '',
  currencyCode: '',
  amount: NaN,
  date: new Date(0),
  subscriptionPeriod: undefined,
  domainName: '',
  quantity: NaN,
  invoiceNumber: '',
  accountEmailMasked: '',
  paymentMethod: {
    label: '',
    type: '',
  },
};

export const formatChargeInfoDto = (chargeInfo: ChargeInfo): TChargeDetails => {
  if (!chargeInfo?.items?.length) {
    return emptyCharge;
  }
  return {
    isConsolidated: chargeInfo?.items?.length > 1,
    domainName: chargeInfo?.items[0].domainName ?? '',
    quantity: chargeInfo?.items[0].quantity ?? NaN,
    service:
      (chargeInfo?.items[0].asset?.type! as PremiumAssetType) ??
      PremiumAssetType.UNKNOWN,
    currencyCode: chargeInfo?.currencyCode ?? '',
    description: chargeInfo?.items[0].asset?.description ?? '',
    amount: chargeInfo?.totalAmount! ?? NaN,
    date: chargeInfo?.date ?? new Date(NaN),
    paymentMethod: {
      type: chargeInfo?.paymentMethod?.type!,
      label: chargeInfo?.paymentMethod?.label!,
    },
    subscriptionPeriod:
      chargeInfo?.items![0].subscription &&
      chargeInfo?.items![0].subscription.endTime
        ? chargeInfo?.items![0].subscription
        : undefined,
    invoiceNumber: chargeInfo?.invoiceNumber ?? '',
    // TODO(PREM2-30172): drop this cast once
    // @wix/ambassador-serverless-charge-locator-service is republished
    // with the regenerated proto types. Until then the local TS doesn't
    // know about the new account_email_masked field.
    accountEmailMasked:
      (chargeInfo as { accountEmailMasked?: string | null })
        ?.accountEmailMasked ?? '',
  };
};

export const UnknownChargeContext = React.createContext<{
  chargeDetails: TChargeDetails;
  pageErrors: TPageErrors;
  setPageErrors(_e: TPageErrors): void;
  setChargeInfo(c: ChargeInfo | {}): void;
}>({
  chargeDetails: formatChargeInfoDto({}),
  setChargeInfo: (_chargeInfo: ChargeInfo) => {},
  setPageErrors: (_e: TPageErrors) => {},
  pageErrors: {} as TPageErrors,
});

export const useUnknownChargeContext = () => {
  return useContext(UnknownChargeContext);
};

export const UnknownChargeContextProvider: React.FC<{
  chargeDetails?: TChargeDetails;
}> = ({ children, chargeDetails: initialChargeDetails }) => {
  const [chargeDetails, setChargeDetails] = useState<TChargeDetails>(
    formatChargeInfoDto({}),
  );
  const [pageErrors, setPageErrors] = useState<TPageErrors>({
    hasError: false,
    message: '',
  });

  return (
    <UnknownChargeContext.Provider
      value={{
        chargeDetails: initialChargeDetails || chargeDetails,
        pageErrors,
        setPageErrors,
        setChargeInfo: (chargeInfo) =>
          setChargeDetails(formatChargeInfoDto(chargeInfo)),
      }}
    >
      {children}
    </UnknownChargeContext.Provider>
  );
};
